import { Country } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { groupBy } from '@backmarket/utils/object/groupBy'

import type {
  Day,
  PutCarrierWeekendDeliveriesConfigurationBody,
} from '~/api/shipping/carrier.types'
import { useSellerInfoStore } from '~/scopes/auth/stores/sellerInfoStore'
import type { WeekendDeliveryConfiguration } from '~/scopes/options/pages/tabs/Shipping/components/ManageWeekendDelivery/ManageWeekendDelivery.types'

import translations from '../ManageWeekendDelivery.translations'

export function useUpdateCarrierWeekendDeliveriesConfiguration() {
  const i18n = useI18n()
  const $httpFetch = useHttpFetch()
  const logger = useLogger()
  const { openSuccessToast, openErrorToast } = useTheToast()
  const { publicId: sellerPublicId } = useSellerInfoStore()

  return async (
    initialCarrierWeekendDeliveryConfigurations: Array<WeekendDeliveryConfiguration>,
    updatedCarrierWeekendDeliveryConfigurations: Array<WeekendDeliveryConfiguration>,
  ) => {
    const changedCarrierWeekendDeliveryConfigurations =
      updatedCarrierWeekendDeliveryConfigurations.filter((configuration) => {
        return !initialCarrierWeekendDeliveryConfigurations.some(
          ({ day, carrierCodename, workingDay, id, countryCode }) =>
            configuration.day === day &&
            configuration.carrierCodename === carrierCodename &&
            configuration.workingDay === workingDay &&
            configuration.id === id &&
            configuration.countryCode === countryCode,
        )
      })

    const weekendDeliveryConfigurationsByCarrier = groupBy(
      changedCarrierWeekendDeliveryConfigurations,
      ({ carrierCodename }) => carrierCodename,
    )
    const body: PutCarrierWeekendDeliveriesConfigurationBody = Object.entries(
      weekendDeliveryConfigurationsByCarrier,
    ).map(([carrierCodename, carrierConfigurations]) => {
      const carrierConfigurationsByCountryCode = groupBy(
        carrierConfigurations,
        ({ countryCode }) => countryCode,
      )
      const weekendDeliveryConfiguration =
        updatedCarrierWeekendDeliveryConfigurations.find(
          (configuration) => configuration.carrierCodename === carrierCodename,
        )

      return {
        carrierCodename,
        carrierName: `${weekendDeliveryConfiguration?.carrierName}`,
        configurations: Object.entries(carrierConfigurationsByCountryCode).map(
          ([countryCode, countryCodeConfigurations]) => {
            const { id } = countryCodeConfigurations[0]
            const countryCodeConfigurationsByDay = groupBy(
              countryCodeConfigurations,
              ({ day }) => day,
            )

            return {
              id,
              countryCode: countryCode as Country,
              weekendDeliveries: Object.entries(
                countryCodeConfigurationsByDay,
              ).map(([day, configurations]) => ({
                day: day as Day,
                workingDay: configurations[0].workingDay,
              })),
            }
          },
        ),
      }
    })

    try {
      if (!sellerPublicId) {
        throw new Error(
          '[Shipping][Weekend delivery][PUT] No seller id provided',
        )
      }

      await $httpFetch(
        '/bm/shipping/carriers/v1/sellers/:sellerPublicId/carrier-weekend-deliveries/configuration',
        {
          method: 'PUT',
          pathParams: { sellerPublicId },
          body,
        },
      )

      openSuccessToast({
        title: i18n(
          translations.manageWeekendDeliveryUpdateConfigurationSuccessTitle,
        ),
        content: i18n(
          translations.manageWeekendDeliveryUpdateConfigurationSuccessContent,
        ),
      })
    } catch (err) {
      logger.error('Cannot retrieve carrier weekend deliveries configuration', {
        error: err as Error,
        sellerPublicId,
        owners: ['bot-squad-shipping-front'],
      })
      openErrorToast({
        title: i18n(
          translations.manageWeekendDeliveryUpdateConfigurationErrorTitle,
        ),
        content: i18n(
          translations.manageWeekendDeliveryUpdateConfigurationErrorContent,
        ),
      })

      throw err
    }
  }
}
